import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Stack,
    Alert,
    Box,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useGetCategoriesForSelect } from '../../../hooks/categories/useGetCategoriesForSelect';
import { useGetTagsForSelect } from '../../../hooks/tags/useGetTagsForSelect';

const STATUS_OPTIONS = [
    { value: 'active',   labelEn: 'Active',   labelAr: 'نشط' },
    { value: 'draft',    labelEn: 'Draft',    labelAr: 'مسودة' },
    { value: 'archived', labelEn: 'Archived', labelAr: 'مؤرشف' },
];

// ----------------------------------------------------------------------
// Status modal
// ----------------------------------------------------------------------

export const ProductsBulkStatusDialog = ({ open, count, onClose, onConfirm, isSubmitting }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const [status, setStatus] = useState('active');

    useEffect(() => {
        if (open) setStatus('active');
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 700 }}>
                {isArabic ? 'تحديث حالة المنتجات' : 'Update product status'}
            </DialogTitle>
            <DialogContent dividers>
                <Alert severity="info" sx={{ mb: 2 }}>
                    {isArabic
                        ? `سيتم تحديث ${count} منتج إلى الحالة المختارة.`
                        : `${count} product(s) will be updated to the selected status.`}
                </Alert>
                <TextField
                    select
                    fullWidth
                    label={isArabic ? 'الحالة' : 'Status'}
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                >
                    {STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            {isArabic ? opt.labelAr : opt.labelEn}
                        </MenuItem>
                    ))}
                </TextField>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={isSubmitting}>
                    {isArabic ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => onConfirm({ status })}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (isArabic ? 'جاري...' : 'Working...') : (isArabic ? 'تطبيق' : 'Apply')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ----------------------------------------------------------------------
// Price-adjust modal
// ----------------------------------------------------------------------

export const ProductsBulkPriceAdjustDialog = ({ open, count, onClose, onConfirm, isSubmitting }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const [operation, setOperation] = useState('percent');
    const [value, setValue] = useState('');

    useEffect(() => {
        if (open) {
            setOperation('percent');
            setValue('');
        }
    }, [open]);

    const numeric = parseFloat(value);
    const valid = Number.isFinite(numeric);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 700 }}>
                {isArabic ? 'تعديل أسعار المنتجات' : 'Adjust product prices'}
            </DialogTitle>
            <DialogContent dividers>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {isArabic
                        ? `سيتم تعديل سعر ${count} منتج. يمكن استخدام قيم سالبة لتقليل السعر.`
                        : `Adjusting price for ${count} product(s). Use negative values to decrease.`}
                </Alert>
                <Stack spacing={2}>
                    <RadioGroup
                        row
                        value={operation}
                        onChange={(e) => setOperation(e.target.value)}
                    >
                        <FormControlLabel
                            value="percent"
                            control={<Radio />}
                            label={isArabic ? 'نسبة مئوية (%)' : 'Percent (%)'}
                        />
                        <FormControlLabel
                            value="absolute"
                            control={<Radio />}
                            label={isArabic ? 'قيمة ثابتة' : 'Absolute'}
                        />
                    </RadioGroup>
                    <TextField
                        type="number"
                        fullWidth
                        label={isArabic ? 'القيمة' : 'Value'}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        inputProps={{ step: 0.01 }}
                        helperText={
                            operation === 'percent'
                                ? (isArabic ? 'مثال: 10 = زيادة 10٪، -5 = خصم 5٪' : 'e.g. 10 = +10%, -5 = -5%')
                                : (isArabic ? 'سيُضاف أو يُطرح هذا المبلغ من سعر كل منتج' : 'Added to (or subtracted from) each product price')
                        }
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={isSubmitting}>
                    {isArabic ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => onConfirm({ operation, value: numeric })}
                    disabled={isSubmitting || !valid}
                >
                    {isSubmitting ? (isArabic ? 'جاري...' : 'Working...') : (isArabic ? 'تطبيق' : 'Apply')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ----------------------------------------------------------------------
// Assign category modal
// ----------------------------------------------------------------------

export const ProductsBulkAssignCategoryDialog = ({ open, count, onClose, onConfirm, isSubmitting }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const [categoryId, setCategoryId] = useState('');

    const { options: categories } = useGetCategoriesForSelect({ per_page: 200 });

    useEffect(() => {
        if (open) setCategoryId('');
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 700 }}>
                {isArabic ? 'تعيين فئة' : 'Assign category'}
            </DialogTitle>
            <DialogContent dividers>
                <Alert severity="info" sx={{ mb: 2 }}>
                    {isArabic
                        ? `سيتم تعيين الفئة المختارة لـ ${count} منتج.`
                        : `The selected category will be assigned to ${count} product(s).`}
                </Alert>
                <TextField
                    select
                    fullWidth
                    label={isArabic ? 'الفئة' : 'Category'}
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                >
                    {(categories || []).map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                            {c.name?.[isArabic ? 'ar' : 'en'] || c.name?.en || c.name || c.id}
                        </MenuItem>
                    ))}
                </TextField>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={isSubmitting}>
                    {isArabic ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => onConfirm({ category_id: categoryId })}
                    disabled={isSubmitting || !categoryId}
                >
                    {isSubmitting ? (isArabic ? 'جاري...' : 'Working...') : (isArabic ? 'تطبيق' : 'Apply')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ----------------------------------------------------------------------
// Assign tag modal
// ----------------------------------------------------------------------

export const ProductsBulkAssignTagDialog = ({ open, count, onClose, onConfirm, isSubmitting }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const [tagId, setTagId] = useState('');
    const [mode, setMode] = useState('add');

    const { options: tags } = useGetTagsForSelect();

    useEffect(() => {
        if (open) {
            setTagId('');
            setMode('add');
        }
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 700 }}>
                {isArabic ? 'تعيين وسم' : 'Assign tag'}
            </DialogTitle>
            <DialogContent dividers>
                <Alert severity="info" sx={{ mb: 2 }}>
                    {isArabic
                        ? `سيتم تعديل وسوم ${count} منتج وفقاً للوضع المختار.`
                        : `Tags will be updated on ${count} product(s) per the selected mode.`}
                </Alert>
                <Stack spacing={2}>
                    <TextField
                        select
                        fullWidth
                        label={isArabic ? 'الوسم' : 'Tag'}
                        value={tagId}
                        onChange={(e) => setTagId(e.target.value)}
                    >
                        {(tags || []).map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                                {t.name?.[isArabic ? 'ar' : 'en'] || t.name?.en || t.name || t.id}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                            {isArabic ? 'الوضع' : 'Mode'}
                        </Typography>
                        <ToggleButtonGroup
                            value={mode}
                            exclusive
                            onChange={(_e, val) => val && setMode(val)}
                            size="small"
                        >
                            <ToggleButton value="add">
                                {isArabic ? 'إضافة' : 'Add'}
                            </ToggleButton>
                            <ToggleButton value="replace">
                                {isArabic ? 'استبدال' : 'Replace'}
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                            {mode === 'add'
                                ? (isArabic ? 'يُضاف الوسم إلى الوسوم الموجودة' : 'Adds the tag alongside existing tags')
                                : (isArabic ? 'يستبدل كل الوسوم بهذا الوسم فقط' : 'Replaces all existing tags with this one')}
                        </Typography>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={isSubmitting}>
                    {isArabic ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => onConfirm({ tag_id: tagId, mode })}
                    disabled={isSubmitting || !tagId}
                >
                    {isSubmitting ? (isArabic ? 'جاري...' : 'Working...') : (isArabic ? 'تطبيق' : 'Apply')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
